import { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  BarChart3, RefreshCw, ShoppingCart, FileText, ChevronRight, AlertTriangle,
  Trophy, Package, Briefcase, Hourglass, SlidersHorizontal,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AbgleichsquoteCard from '@/components/bank/AbgleichsquoteCard';
import { createPageUrl } from '@/utils';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { isBestellung } from '@/lib/orderFields';
import { completeLieferungTasksForLead } from '@/components/tasks/TaskStateMachine';
import {
  summarize, buildSeries, topCustomers, topProducts, newCustomers,
  agentRanking, dormantCustomers, statusBreakdown,
} from '@/lib/dashboardAnalytics';
import { visibleWidgets, widgetSpanClass } from '@/lib/dashboardLayout';
import { useDashboardRange } from '@/components/dashboard/useDashboardRange';
import { useDashboardLayout } from '@/components/dashboard/useDashboardLayout';
import DashboardLayoutEditor from '@/components/dashboard/DashboardLayoutEditor';
import RangeBar from '@/components/dashboard/RangeBar';
import KpiBand from '@/components/dashboard/KpiBand';
import RevenueChart from '@/components/dashboard/RevenueChart';
import RankList from '@/components/dashboard/RankList';
import NewCustomersPanel from '@/components/dashboard/NewCustomersPanel';
import StatusPipeline from '@/components/dashboard/StatusPipeline';
import OpenOrdersPanel from '@/components/dashboard/OpenOrdersPanel';
import RecentContactsPanel from '@/components/dashboard/RecentContactsPanel';
import { eur, num } from '@/components/dashboard/format';

const DORMANT_DAYS = 60;
const EMPTY = Object.freeze([]);
const plural = (n, one, many) => `${num(n)} ${n === 1 ? one : many}`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { range, preset, rawFrom, rawTo, update } = useDashboardRange();
  const { layout } = useDashboardLayout();
  const [layoutOpen, setLayoutOpen] = useState(false);

  const leadsQ = useQuery({
    queryKey: ['admin-leads'],
    queryFn: () => base44.entities.Lead.list('name'),
  });
  const ordersQ = useQuery({
    queryKey: ['admin-orders'],
    queryFn: () => base44.entities.Order.list('-created_date'),
  });
  const activitiesQ = useQuery({
    queryKey: ['admin-activities'],
    queryFn: () => base44.entities.ActivityReport.list('-report_date'),
  });

  const leads = leadsQ.data ?? EMPTY;
  const orders = ordersQ.data ?? EMPTY;
  const activities = activitiesQ.data ?? EMPTY;
  const loading = ordersQ.isLoading || leadsQ.isLoading;
  const loadError = ordersQ.error || leadsQ.error;
  const refreshing = ordersQ.isFetching || leadsQ.isFetching;

  const refetchAll = () => {
    leadsQ.refetch();
    ordersQ.refetch();
    activitiesQ.refetch();
    queryClient.invalidateQueries(['admin-tasks']);
  };

  // Mutation: mark a Bestellung as Abgeschlossen (completed, no data loss)
  const completeOrderMutation = useMutation({
    mutationFn: async (orderId) => {
      const updated = await base44.entities.Order.update(orderId, { status: 'Abgeschlossen' });
      // Açık "Lieferung" To-Do'su otomatik kapanır
      completeLieferungTasksForLead({
        leadId: updated.lead_id,
        resultText: `Bestellung ${updated.order_number || ''} abgeschlossen (Admin)`,
      }).catch(() => {});
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-orders']);
      queryClient.invalidateQueries(['admin-tasks']);
      toast.success('Bestellung abgeschlossen ✓');
    },
    onError: (err) => {
      toast.error('Fehler: ' + err.message);
    }
  });

  const openOrders = useMemo(() => orders.filter((o) => isBestellung(o) && o.status === 'Neu'), [orders]);
  const pendingReviewCount = useMemo(() => orders.filter((o) => o.order_status === 'pending_review').length, [orders]);

  // Alle Kennzahlen aus einem Durchlauf je Zeitraum (reine Funktionen, keine Schreibzugriffe)
  const a = useMemo(() => {
    const current = summarize(orders, range.from, range.to);
    const previous = summarize(orders, range.prevFrom, range.prevTo);
    const fresh = newCustomers(orders, leads, range.from, range.to);
    const prevFresh = newCustomers(orders, leads, range.prevFrom, range.prevTo);
    const inRange = (raw) => { const d = raw ? new Date(raw) : null; return d && d >= range.from && d <= range.to; };
    return {
      current,
      previous,
      fresh,
      prevNewCount: prevFresh.list.length,
      series: buildSeries(orders, range),
      customers: topCustomers(orders, range.from, range.to, 50),
      products: topProducts(orders, range.from, range.to, 50),
      agents: agentRanking(orders, range.from, range.to),
      dormant: dormantCustomers(orders, new Date(), DORMANT_DAYS, 20),
      statuses: statusBreakdown(orders, range.from, range.to),
      reports: activities.filter((r) => inRange(r.report_date)).length,
    };
  }, [orders, leads, activities, range]);

  const openContacts = () => navigate(createPageUrl('AdminLeads'));
  const openOrder = (order) => navigate(createPageUrl('AdminOrders'), { state: { openOrder: order } });
  const recentLeads = useMemo(() => leads
    .slice()
    .sort((x, y) => new Date(y.created_date || 0) - new Date(x.created_date || 0))
    .slice(0, 5), [leads]);

  // Widget-Registry: ID (lib/dashboardLayout) → Inhalt. Reihenfolge/Sichtbarkeit
  // kommt aus dem persönlichen Layout des Admins.
  const widgets = {
    kpis: (
      <KpiBand
        loading={loading}
        current={a.current}
        previous={a.previous}
        newCount={a.fresh.list.length}
        prevNewCount={a.prevNewCount}
        newContacts={a.fresh.newContacts}
        series={a.series.points}
      />
    ),
    abgleich: <AbgleichsquoteCard />,
    umsatz: <RevenueChart loading={loading} series={a.series} />,
    neukunden: <NewCustomersPanel loading={loading} list={a.fresh.list} newContacts={a.fresh.newContacts} onSelect={openContacts} />,
    offene: (
      <OpenOrdersPanel
        loading={ordersQ.isLoading}
        orders={openOrders}
        onOpen={openOrder}
        onComplete={(id) => completeOrderMutation.mutate(id)}
        completing={completeOrderMutation.isPending}
      />
    ),
    status: <StatusPipeline loading={loading} rows={a.statuses} />,
    kontakte: <RecentContactsPanel loading={leadsQ.isLoading} leads={recentLeads} />,
    topkunden: (
      <RankList
        overline="Nach Umsatz im Zeitraum"
        title="Top Kunden"
        loading={loading}
        rows={a.customers.map((c) => ({
          key: c.key,
          name: c.name,
          value: c.revenue,
          sub: `${plural(c.orders, 'Bestellung', 'Bestellungen')} · ${num(c.share * 100)} % Anteil`,
        }))}
        formatValue={eur}
        onSelect={openContacts}
        emptyIcon={<Trophy className="w-8 h-8" />}
        emptyTitle="Noch keine Umsätze im Zeitraum"
      />
    ),
    topprodukte: (
      <RankList
        overline="Nach Umsatz im Zeitraum"
        title="Top Produkte"
        loading={loading}
        rows={a.products.map((p) => ({
          key: p.key,
          name: p.name,
          value: p.revenue,
          sub: `${num(p.quantity)} Stück · in ${plural(p.orders, 'Bestellung', 'Bestellungen')}`,
        }))}
        formatValue={eur}
        emptyIcon={<Package className="w-8 h-8" />}
        emptyTitle="Keine Positionen im Zeitraum"
        emptyHint="Produkte erscheinen, sobald Bestellungen mit Positionen vorliegen."
      />
    ),
    aussendienst: (
      <RankList
        overline="Bestellungen mit Außendienst"
        title="Außendienst"
        meta={!loading && <span className="text-xs text-slate-400 tabular-nums">{num(a.reports)} Besuchsberichte</span>}
        loading={loading}
        rows={a.agents.map((g) => ({
          key: g.key,
          name: g.name,
          value: g.revenue,
          sub: plural(g.orders, 'Bestellung', 'Bestellungen'),
        }))}
        formatValue={eur}
        emptyIcon={<Briefcase className="w-8 h-8" />}
        emptyTitle="Keine Außendienst-Bestellungen im Zeitraum"
      />
    ),
    reaktivieren: (
      <RankList
        overline={`Stammkunden ohne Bestellung seit ${DORMANT_DAYS}+ Tagen`}
        title="Reaktivieren"
        loading={loading}
        rows={a.dormant.map((c) => ({
          key: c.key,
          name: c.name,
          value: c.revenue,
          sub: `letzte Bestellung vor ${num(c.daysSince)} Tagen · ${plural(c.orders, 'Bestellung', 'Bestellungen')} gesamt`,
        }))}
        formatValue={eur}
        onSelect={openContacts}
        emptyIcon={<Hourglass className="w-8 h-8" />}
        emptyTitle="Alle Stammkunden bestellen regelmäßig"
        emptyHint={`Kunden mit mindestens zwei Bestellungen und ${DORMANT_DAYS} Tagen Pause erscheinen hier.`}
      />
    ),
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <PageHeader
              icon={<BarChart3 className="w-5 h-5" />}
              title="Dashboard"
              subtitle="Umsatz, Kunden und Aufträge im gewählten Zeitraum"
              subtitleClassName="hidden sm:block"
            />
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              {openOrders.length > 0 && (
                <Link
                  to={createPageUrl('AdminOrders')}
                  aria-label={`${openOrders.length} offene Bestellungen`}
                  className="relative inline-flex w-11 h-11 sm:w-10 sm:h-10 items-center justify-center rounded-lg border border-slate-200 text-brand-900 hover:bg-slate-50 transition-colors"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-brand-700 text-white text-[11px] font-bold tabular-nums">
                    {openOrders.length}
                  </span>
                </Link>
              )}
              <button
                type="button"
                onClick={() => setLayoutOpen(true)}
                aria-label="Dashboard anpassen"
                title="Dashboard anpassen"
                className="inline-flex w-11 h-11 sm:w-10 sm:h-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 active:scale-[0.97] transition"
              >
                <SlidersHorizontal className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={refetchAll}
                aria-label="Daten aktualisieren"
                className="inline-flex w-11 h-11 sm:w-10 sm:h-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 active:scale-[0.97] transition"
              >
                <RefreshCw className={cn('w-5 h-5', refreshing && 'motion-safe:animate-spin')} />
              </button>
            </div>
          </div>
          <RangeBar range={range} preset={preset} rawFrom={rawFrom} rawTo={rawTo} onChange={update} />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-5 md:pt-6 pb-10 md:pb-14 safe-area-inset-bottom space-y-4 md:space-y-5">
        {loadError && (
          <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-sm">
              <p className="font-semibold text-red-800">Daten konnten nicht geladen werden</p>
              <p className="text-red-700 break-words">{loadError.message}</p>
            </div>
            <button type="button" onClick={refetchAll} className="shrink-0 text-sm font-semibold text-red-800 underline underline-offset-2">
              Erneut versuchen
            </button>
          </div>
        )}

        {pendingReviewCount > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-200 border-l-4 border-l-amber-600 bg-amber-50 px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-amber-700 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">Genehmigung erforderlich</p>
                <p className="text-sm text-amber-800">{plural(pendingReviewCount, 'Angebot wartet', 'Angebote warten')} auf Ihre Überprüfung</p>
              </div>
            </div>
            <Link
              to={createPageUrl('AdminOrders')}
              className="inline-flex items-center justify-center gap-1 h-11 sm:h-9 px-4 rounded-lg bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 active:scale-[0.98] transition"
            >
              Überprüfen <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Persönliches Layout: 6er-Raster auf Desktop, eine Spalte mobil.
            `empty:hidden` lässt Zellen verschwinden, deren Widget nichts rendert
            (z. B. Zahlungsabgleich ohne Buchhaltungs-Migration). */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 md:gap-5 items-start">
          {visibleWidgets(layout).map((w) => (
            <div key={w.id} className={cn('min-w-0 empty:hidden', widgetSpanClass(w.span))}>
              {widgets[w.id]}
            </div>
          ))}
        </div>
      </main>

      <Sheet open={layoutOpen} onOpenChange={setLayoutOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
          <SheetHeader className="px-5 pt-5 pb-3 text-left border-b border-slate-100">
            <SheetTitle className="font-display text-lg font-bold tracking-tight text-slate-900">Dashboard anpassen</SheetTitle>
            <SheetDescription className="text-sm text-slate-500">
              Gilt für Ihr Konto. Auch unter System → Einstellungen.
            </SheetDescription>
          </SheetHeader>
          <div className="px-5 py-4">
            <DashboardLayoutEditor />
            <Link
              to={createPageUrl('AdminEinstellungen')}
              onClick={() => setLayoutOpen(false)}
              className="mt-4 inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-brand-900 hover:underline"
            >
              Alle Einstellungen <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
